import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en from '../locales/en.json';
import ja from '../locales/ja.json';
import zh from '../locales/zh.json';

const translations = { en, ja, zh };

// Single source of truth for the supported UI languages. To add a locale:
// import its JSON, add it to `translations` above, and append an entry here —
// it is then accepted everywhere and rendered by <LanguageSwitch/> automatically
// (no per-call-site language list to keep in sync). `label` is a short abbreviation
// shown on the compact trigger button; `nativeName` is the language's full name in
// its own script, shown in the dropdown list. Both are intentionally untranslated.
// `htmlLang` is the BCP 47 tag written to <html lang>, which screen readers and
// the browser's choice of CJK glyphs follow.
const SUPPORTED_LANGUAGES = [
  { value: 'zh', label: '繁中', nativeName: '繁體中文', htmlLang: 'zh-Hant' },
  { value: 'en', label: 'EN', nativeName: 'English', htmlLang: 'en' },
  { value: 'ja', label: '日本語', nativeName: '日本語', htmlLang: 'ja' },
];
const DEFAULT_LANGUAGE = 'en';
const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((option) => option.value);
const isSupportedLanguage = (language) => SUPPORTED_LANGUAGE_CODES.includes(language);
const normalizeLanguage = (language) => (isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE);

// One browser locale tag → a UI language, or '' when we have none for it. Our
// Chinese copy is Traditional, so only Traditional Chinese maps to `zh`;
// Simplified Chinese, and a bare `zh` (which maximizes to Hans), get English.
const languageFromLocale = (tag) => {
  let locale;
  try {
    locale = new Intl.Locale(tag).maximize();
  } catch {
    return '';
  }
  if (locale.language === 'zh') return locale.script === 'Hant' ? 'zh' : 'en';
  return isSupportedLanguage(locale.language) ? locale.language : '';
};

// First browser language we can serve, in the browser's own order; English
// when none matches.
const detectBrowserLanguage = (languages) => {
  for (const tag of languages || []) {
    const language = languageFromLocale(tag);
    if (language) return language;
  }
  return DEFAULT_LANGUAGE;
};

const getBrowserLanguages = () => {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages?.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
};

const STORAGE_KEY = 'lang';
const listeners = new Set();

// Storage can be blocked (private modes, site-data settings); the choice then
// lasts for this page load only.
const readStoredLanguage = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredLanguage = (language) => {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Keep the in-memory choice.
  }
};

const applyDocumentLanguage = (language) => {
  if (typeof document === 'undefined') return;
  const option = SUPPORTED_LANGUAGES.find((entry) => entry.value === language);
  document.documentElement.lang = option?.htmlLang || language;
};

// A language the user picked wins; otherwise follow the browser.
const storedLanguage = readStoredLanguage();
let currentLanguage = isSupportedLanguage(storedLanguage)
  ? storedLanguage
  : detectBrowserLanguage(getBrowserLanguages());
applyDocumentLanguage(currentLanguage);

const translateKey = (language, key) => translations[language]?.[key] || key;

const I18nContext = createContext({
  language: currentLanguage,
  setLanguage: () => {},
  t: (key) => translateKey(currentLanguage, key),
});

const notifyLanguageChange = (language) => {
  listeners.forEach((listener) => listener(language));
};

const getLanguage = () => currentLanguage;

const setLanguage = (language) => {
  currentLanguage = normalizeLanguage(language);
  writeStoredLanguage(currentLanguage);
  applyDocumentLanguage(currentLanguage);
  notifyLanguageChange(currentLanguage);
};

const t = (key, language = currentLanguage) => translateKey(language, key);

const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(currentLanguage);

  useEffect(() => {
    const handleLanguageChange = (nextLanguage) => {
      setLanguageState(nextLanguage);
    };
    listeners.add(handleLanguageChange);
    return () => listeners.delete(handleLanguageChange);
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: (key) => translateKey(language, key),
    }),
    [language]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

const useI18n = () => useContext(I18nContext);

export { I18nProvider, getLanguage, setLanguage, t, useI18n, detectBrowserLanguage, SUPPORTED_LANGUAGES };
